<template>
  <div
    v-if="enabled"
    class="buy-now"
  >
    <transition
      name="fade"
      mode="out-in"
    >
      <btn
        v-if="!showPayments"
        class="buy-now__btn buy-now__toggle"
        :label="$string('buy_now.express_payment')"
        modifiers="secondary block"
        show-icon
        type="button"
        aria-label="Show express payment options"
        @click="handleShowPayments"
      >
        <template #icon>
          <icon-credit-card />
        </template>
      </btn>

      <div
        v-else
        class="flex buy-now__payment-options"
      >
        <btn
          class="buy-now__btn"
          modifiers="secondary block center"
          show-icon
          label="Pay with Shop Pay"
          class-label="visually-hidden"
          type="button"
          :disabled="loading"
          @click="handleBuyClick('buy_now_shop_pay_click', 'shop_pay')"
        >
          <template #icon>
            <icon-shop-pay />
          </template>
        </btn>

        <btn
          class="buy-now__btn"
          :label="$string('buy_now.quick_buy')"
          modifiers="secondary block reversed center"
          show-icon
          type="button"
          :disabled="loading"
          @click="handleBuyClick('buy_now_quick_buy_click')"
        >
          <template #icon>
            <icon-credit-card />
          </template>
        </btn>

        <btn
          class="buy-now__btn buy-now__close"
          show-icon
          type="button"
          modifiers="primary round"
          label="Hide express payment options"
          :disabled="loading"
          @click="handleClosePayments"
        >
          <template #icon>
            <icon-close />
          </template>
        </btn>
      </div>
    </transition>
  </div>
</template>

<script>

/**
 * Vue: Buy now (buy-now)
 * -----------------------------------------------------------------------------
 * Buy now options on the PDP.
 *
 */

import { track } from '~/helpers/datalayer-actions'
import Btn from '~global/btn/btn'

import IconClose from '~icons/directional-navigation/close.svg'
import IconCreditCard from '~icons/general/credit-card.svg'
import IconShopPay from '~icons/general/shop-pay.svg'

import { convertToShopifyId } from '~/helpers/convert'
import { getVariables } from '~/helpers/graphql'

import cartCreateMutation from '~graphql/cartCreate.gql'

export default {
  name: 'BuyNow',

  components: {
    Btn,
    IconCreditCard,
    IconClose,
    IconShopPay,
  },

  props: {
    lineItemProperties: {
      type: Object,
      default: () => ({}),
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    addon: {
      type: [null, Object],
      default: null,
    },
  },

  emits: {
    'missing-variant': () => true,
  },

  data() {
    return {
      showPayments: false,
      enabled: false,
      loading: false,
    }
  },

  created() {
    if (window.buyNowAb) {
      this.enabled = true
    }

    window.addEventListener('buy-now-ab', () => {
      this.enabled = true
    })

    window.addEventListener('buy-now-ab-deactivate', () => {
      this.enabled = false
    })
  },

  methods: {
    track,

    /**
     * Show payments and track click
     */
    handleShowPayments() {
      if (!this.variant) {
        this.$emit('missing-variant')
        return
      }
      this.showPayments = true
      track('buy_now_open_click')
    },

    /**
     * Hide payments and track click
     */
    handleClosePayments() {
      this.showPayments = false
      track('buy_now_close_click')
    },

    /**
     * Create a new cart with storefront API and redirect to the checkout url with the selected
     * product(s)
     * @param {string} gtmEvent The analytics event to track
     * @param {string} method Accepted value: shop_pay
     */
    async handleBuyClick(gtmEvent, method) {
      try {
        this.loading = true

        const properties = Object.entries(this.lineItemProperties).map((entry) => {
          return {
            key: entry[0],
            value: String(entry[1]),
          }
        })

        const lines = [{
          quantity: 1,
          merchandiseId: convertToShopifyId('ProductVariant', this.variant.id),
          attributes: properties,
        }]

        if (this.addon?.variant) {
          lines.push({
            quantity: 1,
            merchandiseId: convertToShopifyId('ProductVariant', this.addon.variant.id),
          })
        }

        const buyerIdentity = {
          countryCode: this.$variable('store.country'),
        }

        if (method === 'shop_pay') {
          buyerIdentity.preferences = {
            wallet: ['shop_pay'],
          }
        }

        const response = await cnvs.Query({
          query: cartCreateMutation,
          variables: getVariables({
            input: {
              attributes: [{
                key: 'gift_note',
                value: '</gift_note>',
              }],
              buyerIdentity,
              lines,
            },
          }),
        })

        const checkoutUrl = response.cartCreate.cart.checkoutUrl
        track(gtmEvent)
        window.location = checkoutUrl
      } catch (error) {
        console.error(error)
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import './buy-now';
</style>
